/**
 * 航班费用详情抽屉 V2 - 集成 Agent 验证结果
 *
 * 设计原则：
 * - 快速打开关闭
 * - 展示选中航班的完整费用明细
 * - 叠加 Agent 验证鉴定结论（完整性检查、重复检查、逐项鉴定）
 */

import React from 'react';
import { Drawer, Descriptions, Table, Typography, Tag, Empty, Alert, Divider, Tooltip, Button } from 'antd';
import AgentComparisonMatrix, { extractProductsMatrix } from '../../../components/AgentComparisonMatrix';
import {
  RocketOutlined,
  CalendarOutlined,
  EnvironmentOutlined,
  LinkOutlined,
  CheckCircleFilled,
  CloseCircleFilled,
  WarningFilled,
  CalculatorOutlined,
  SafetyOutlined,
} from '@ant-design/icons';
import { AirplaneOutlined } from '../../../../../components/icons/SvgIcons';
import { compareSourceDisplayOrder, getSourceVisual, sortBySourceDisplayOrder } from './sourceVisuals.js';
import { buildCurrencyConversionDisplay } from './currencyDisplay.js';
// V3: APReconciliationPage 不再在此组件内嵌入，
// 而是通过 onInvoiceClick 回调冒泡到工作台级别，复用 InvoiceDetailDrawer。

const { Text, Link } = Typography;

/**
 * 格式化金额
 */
function formatAmount(amount) {
  if (amount === null || amount === undefined) return '—';
  return amount.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function formatDeltaType(deltaType) {
  const labels = {
    UNIT_PRICE_DISPLAY_ROUNDING: '单价展示尾差',
    FORMULA_BASED_PRICING: '复杂计价',
    ALLOCATION_RESIDUAL: '分摊尾差',
  };
  return labels[deltaType] || deltaType || '金额差异';
}

const collectExecutionFailures = (flightValidation) => {
  const cv = flightValidation?.crossValidation;
  if (!cv && !flightValidation?.executionFailed) return [];
  const explicitFailures = Array.isArray(cv?.execution_failures) ? cv.execution_failures : [];
  const voteFailures = Array.isArray(cv?.votes)
    ? cv.votes
      .filter((vote) => vote?.execution_failed)
      .map((vote) => ({
        task_id: vote.task_id,
        candidate_index: vote.candidate_index,
        execution_id: vote.execution_id,
        terminal_error_type: vote.terminal_error_type,
        failure_reason: vote.failure_reason,
        execution_evidence: vote.execution_evidence,
      }))
    : [];
  const failures = [...explicitFailures, ...voteFailures];
  if (failures.length > 0) {
    const seen = new Set();
    return failures.filter((failure) => {
      const key = [failure?.task_id, failure?.execution_id, failure?.candidate_index].join(':');
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }
  return flightValidation?.executionFailed ? [{}] : [];
};

const ExecutionFailureDetails = ({ failures }) => {
  if (!failures.length) return null;
  return (
    <div style={{ marginTop: 8 }}>
      {failures.map((failure, index) => {
        const evidence = failure.execution_evidence || {};
        const executionId = failure.execution_id || evidence.execution_id;
        const attempts = Array.isArray(evidence.attempts) ? evidence.attempts : [];
        const terminalType = failure.terminal_error_type || evidence.terminal_error_type;
        const message = failure.failure_reason || evidence.terminal_message;
        return (
          <div key={`${failure.task_id || 'unknown'}-${index}`} style={{ marginTop: index === 0 ? 0 : 6, fontSize: 12, lineHeight: 1.6 }}>
            <div>
              <Text strong>任务 {failure.task_id || evidence.attempt_index || index + 1}：</Text>
              {executionId ? (
                <Text code>{executionId}</Text>
              ) : (
                <Text type="danger">未记录外部 execution id，无法确认是否成功发起</Text>
              )}
            </div>
            {(terminalType || message) && (
              <div style={{ color: '#595959' }}>
                {terminalType ? `错误类型：${terminalType}` : ''}{terminalType && message ? '；' : ''}{message || ''}
              </div>
            )}
            {attempts.length > 0 && (
              <div style={{ color: '#8c8c8c' }}>
                Attempts：{attempts.map((attempt) => {
                  const label = `#${attempt.attempt_index || '?'} ${attempt.launch_status || 'UNKNOWN'}`;
                  return attempt.execution_id ? `${label} (${attempt.execution_id})` : label;
                }).join(' / ')}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

/**
 * 验证结果状态 Tag
 */
const ValidationResultTag = ({ result }) => {
  if (!result) return null;

  const config = {
    PASS: { color: 'green', text: '通过' },
    FAIL: { color: 'red', text: '警告' },
    NO_RULE: { color: 'default', text: '无规则' },
  };

  const cfg = config[result] || { color: 'default', text: result };
  return <Tag color={cfg.color} style={{ fontSize: 11, margin: 0 }}>{cfg.text}</Tag>;
};

const FlightValidationResultDisplay = ({ result }) => {
  if (!result) return <span style={{ color: '#d9d9d9' }}>-</span>;

  const isPass = result.match !== false;
  const hasExecutionFailure = result.agent_results?.some((r) => r.execution_failed);
  const agentValues = result.agent_results?.map((r, i) => {
    if (r.execution_failed) return `#${i + 1}: 验证执行失败${r.execution_id ? ` (${r.execution_id})` : ''}`;
    if (r.missing) return `#${i + 1}: 未提取`;
    return `#${i + 1}: ${formatAmount(r.value)}`;
  }).join('\n') || '';

  return (
    <Tooltip
      title={
        <div style={{ maxWidth: 320 }}>
          <div>标准值: {formatAmount(result.standard)}</div>
          {agentValues && <div style={{ marginTop: 4, whiteSpace: 'pre-line' }}>{agentValues}</div>}
        </div>
      }
    >
      <span style={{ cursor: 'pointer' }}>
        <Tag color={hasExecutionFailure ? 'orange' : isPass ? 'green' : 'red'} style={{ fontSize: 11, margin: 0 }}>
          {hasExecutionFailure ? '执行失败' : isPass ? '通过' : '不一致'}
        </Tag>
      </span>
    </Tooltip>
  );
};

/**
 * 检查结果渲染（完整性检查 / 重复检查）
 */
const CheckResultDisplay = ({ label, icon, check }) => {
  if (!check) return null;

  const isPass = check.result === 'PASS';
  const color = isPass ? '#52c41a' : '#ff4d4f';

  return (
    <div style={{ marginBottom: 8 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
        {icon}
        <Text strong style={{ fontSize: 12 }}>{label}</Text>
        {isPass ? (
          <CheckCircleFilled style={{ color, fontSize: 12 }} />
        ) : (
          <CloseCircleFilled style={{ color, fontSize: 12 }} />
        )}
      </div>
      <div className="validation-reasoning" style={{ paddingLeft: 20 }}>
        {check.reasoning}
      </div>
    </div>
  );
};

/**
 * 详情抽屉组件
 */
const DetailDrawer = ({ visible, flight, products, invoiceMap, onClose, flightValidation, onInvoiceClick }) => {
  if (!flight) return null;

  const {
    flight_number,
    flight_date,
    departure_airport,
    arrival_airport,
    aircraft_reg,
    charges_by_product,
    total_amount,
  } = flight;

  // V3: 通过外部回调打开账单详情，复用工作台级别的 InvoiceDetailDrawer
  const handleInvoiceClick = (dataSourceId) => {
    if (!dataSourceId) return;
    if (onInvoiceClick) {
      onInvoiceClick(dataSourceId);
    } else {
      // 降级：无回调时在新标签页打开工作台 IDS 列表页
      window.open(`/settlement/invoice-data-sources`, '_blank');
    }
  };

  // ===== V6.0: 使用统一的 extractProductsMatrix 构建交叉验证数据 =====
  const crossValidationMatrix = (() => {
    const cv = flightValidation?.crossValidation;
    if (!cv) return null;
    return extractProductsMatrix(cv);
  })();

  // 兼容：crossValidationDiff 用于判断是否有交叉验证数据
  const crossValidationDiff = crossValidationMatrix ? Object.keys(crossValidationMatrix).length > 0 : false;
  const executionFailures = collectExecutionFailures(flightValidation);
  const hasExecutionFailure = executionFailures.length > 0;

  // 构建费用明细列表，并将验证结果挂载到每项费用上
  const chargeList = [];
  Object.entries(charges_by_product || {}).forEach(([productCode, charges]) => {
    sortBySourceDisplayOrder(charges, (charge) => charge.source_type).forEach((charge, index) => {
      const productInfo = products?.[productCode] || {};

      // 查找该费用项的验证结果（阶段4.5 batch validation）
      let chargeValidation = null;
      if (flightValidation?.charge_results) {
        const sourceKey = `${charge.invoice_number}/${charge.item_id}`;
        chargeValidation = flightValidation.charge_results.find(
          (cr) => cr.source === sourceKey
        ) || null;
      }

      // 查找该费用项的交叉验证结果（阶段四）
      let crossResult = null;
      if (crossValidationMatrix) {
        crossResult = crossValidationMatrix[productCode] || null;
      }

      chargeList.push({
        key: `${productCode}-${index}`,
        product_code: productCode,
        product_name: productInfo.name || charge.product_name || productCode,
        description: charge.description,
        quantity: charge.quantity,
        unit: charge.unit || productInfo.unit,
        unit_price: charge.unit_price,
        amount: charge.amount,
        currency: charge.currency,
        base_amount: charge.base_amount,
        base_currency: charge.base_currency,
        original_amount: charge.original_amount,
        original_currency: charge.original_currency,
        exchange_rate: charge.exchange_rate,
        conversion_warning: charge.conversion_warning,
        is_reference_charge: charge.is_reference_charge,
        is_current_batch_charge: charge.is_current_batch_charge,
        invoice_number: charge.invoice_number,
        source_type: charge.source_type,
        invoice_data_source_id: charge.invoice_data_source_id,
        delta_type: charge.delta_type,
        amount_delta: charge.amount_delta,
        delta_audit_summary: charge.delta_audit_summary,
        line_amount_delta: charge.line_amount_delta,
        allocation_amount_delta: charge.allocation_amount_delta,
        // 验证结果
        validation: chargeValidation,
        crossResult,
      });
    });
  });

  // 将 FAIL 的项排在前面，方便用户定位
  chargeList.sort((a, b) => {
    const aFail = (a.validation?.result === 'FAIL' || a.crossResult?.match === false) ? 0 : 1;
    const bFail = (b.validation?.result === 'FAIL' || b.crossResult?.match === false) ? 0 : 1;
    if (aFail !== bFail) return aFail - bFail;
    return compareSourceDisplayOrder(a.source_type, b.source_type);
  });

  // 费用明细表格列
  const columns = [
    {
      title: '产品',
      dataIndex: 'product_name',
      key: 'product_name',
      width: 200,
      render: (text, record) => (
        <div>
          <Text strong>{text}</Text>
          {record.description && (
            <div style={{
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {record.description}
              </Text>
            </div>
          )}
          {[
            record.line_amount_delta || (record.delta_type ? {
              delta_type: record.delta_type,
              audit_summary: record.delta_audit_summary,
            } : null),
            record.allocation_amount_delta,
          ].filter(Boolean).map((delta, deltaIndex) => {
            const isAllocationResidual = delta.delta_type === 'ALLOCATION_RESIDUAL';
            const deltaLabel = isAllocationResidual ? '分摊尾差' : `存在金额差异处理：${formatDeltaType(delta.delta_type)}`;
            return (
              <Tooltip key={`${delta.delta_type}-${deltaIndex}`} title={delta.audit_summary || delta.delta_detail?.audit_summary || '该行存在金额差异处理记录。'}>
                <Tag color={isAllocationResidual ? 'purple' : 'gold'} style={{ marginTop: 4, fontSize: 10 }}>
                  {deltaLabel}
                </Tag>
              </Tooltip>
            );
          })}
        </div>
      ),
    },
    {
      title: '数量',
      dataIndex: 'quantity',
      key: 'quantity',
      width: 60,
      align: 'right',
      render: (val, record) => {
        const quantity = val || 1;
        return record.unit ? `${quantity} ${record.unit}` : quantity;
      },
    },
    {
      title: '单价',
      dataIndex: 'unit_price',
      key: 'unit_price',
      width: 80,
      align: 'right',
      render: (val) => formatAmount(val),
    },
    {
      title: '金额',
      dataIndex: 'amount',
      key: 'amount',
      width: 100,
      align: 'right',
      render: (val, record) => {
        const isFail = record.validation?.result === 'FAIL' || record.crossResult?.match === false;
        const isReference = record.is_reference_charge;
        const baseCurrency = record.base_currency || record.currency;
        const conversionDisplay = buildCurrencyConversionDisplay(record, baseCurrency);
        return (
          <div style={{ textAlign: 'right' }}>
            <Text strong style={isReference ? { color: '#8c8c8c' } : isFail ? { color: '#ff4d4f' } : undefined}>
              {isReference ? '参 ' : ''}
              {baseCurrency ? `${baseCurrency} ` : ''}{formatAmount(val)}
            </Text>
            {conversionDisplay && (
              <Tooltip
                title={
                  conversionDisplay.formula ? (
                    <div>
                      <div>{conversionDisplay.formula}</div>
                      <div style={{ marginTop: 4, color: '#d9d9d9' }}>后端汇率仍按 {conversionDisplay.originalCurrency} → {conversionDisplay.baseCurrency} 计算。</div>
                    </div>
                  ) : null
                }
              >
                <div style={{ fontSize: 11, color: '#8c8c8c', cursor: conversionDisplay.formula ? 'help' : 'default' }}>
                  <div>{conversionDisplay.originalLabel}</div>
                  {conversionDisplay.rateLabel && <div>汇率 {conversionDisplay.rateLabel}</div>}
                </div>
              </Tooltip>
            )}
            {record.conversion_warning && (
              <Tooltip title={record.conversion_warning.message || record.conversion_warning.code}>
                <Tag color="warning" style={{ fontSize: 10, margin: 0 }}>折算提示</Tag>
              </Tooltip>
            )}
          </div>
        );
      },
    },
      {
        title: '来源',
        dataIndex: 'source_type',
        key: 'source_type',
        width: 140,
        render: (type, record) => {
        const sourceMeta = getSourceVisual(type);
        // 从统一的 invoiceMap 获取账单标识（Context 层已计算最小区分前缀）
        const invoiceIdentity = record.invoice_data_source_id
            ? invoiceMap?.[record.invoice_data_source_id]
            : null;
        const fullNumber = invoiceIdentity?.invoice_number || record.invoice_number;
        const displayLabel = invoiceIdentity?.short_label || fullNumber;

        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <Tag color={record.is_reference_charge ? 'default' : sourceMeta.tone === 'pending' ? 'blue' : sourceMeta.tone === 'committed' ? 'green' : 'default'} style={{ margin: 0, width: 'fit-content' }}>
              {record.is_reference_charge ? '参照' : sourceMeta.label}
            </Tag>
            {fullNumber && record.invoice_data_source_id ? (
              <Tooltip title={fullNumber}>
                <Link
                  onClick={() => handleInvoiceClick(record.invoice_data_source_id)}
                  style={{ display: 'flex', alignItems: 'center', gap: 4 }}
                >
                  <LinkOutlined />
                  {displayLabel}
                </Link>
              </Tooltip>
            ) : (
              <Text type="secondary" style={{ fontSize: 12 }}>{fullNumber || '—'}</Text>
            )}
          </div>
        );
      },
    },
  ];

  // 验证列：计费规则验证与航班验证分别展示，避免两类口径混在一起。
  const hasAnyValidation = flightValidation?.charge_results || crossValidationMatrix;
  if (hasAnyValidation) {
    columns.push({
      title: '计费规则验证',
      key: 'billing_validation',
      width: 130,
      align: 'center',
      render: (_, record) => {
        if (record.validation) {
          const brief = record.validation.brief;
          return (
            <Tooltip
              title={
                <div style={{ maxWidth: 300 }}>
                  {record.validation.reasoning}
                </div>
              }
            >
              <span style={{ cursor: 'pointer' }}>
                <div><ValidationResultTag result={record.validation.result} /></div>
                {brief && (
                  <div style={{ fontSize: 10, color: '#8c8c8c', marginTop: 2 }}>
                    {brief}
                  </div>
                )}
              </span>
            </Tooltip>
          );
        }
        return <span style={{ color: '#d9d9d9' }}>-</span>;
      },
    }, {
      title: '验证航班',
      key: 'flight_validation',
      width: 120,
      align: 'center',
      render: (_, record) => <FlightValidationResultDisplay result={record.crossResult} />,
    });
  }

  // 验证状态 Banner（合并两个来源）
  const validationBanner = (() => {
    if (!flightValidation) return null;
    const businessStatus = flightValidation.businessValidationStatus;
    const status = hasExecutionFailure
      ? (businessStatus === 'HAS_ISSUES' ? businessStatus : null)
      : (businessStatus || flightValidation.status);

    const statusConfig = {
      HAS_ISSUES: {
        type: 'error',
        icon: <CloseCircleFilled />,
        message: '业务验证警告',
      },
      PASSED_WITH_WARNINGS: {
        type: 'warning',
        icon: <WarningFilled />,
        message: '提醒',
      },
      PASSED: {
        type: 'success',
        icon: <CheckCircleFilled />,
        message: '验证通过',
      },
    };

    const cfg = statusConfig[status];
    if (!cfg) return null;

    return (
      <Alert
        type={cfg.type}
        icon={cfg.icon}
        message={cfg.message}
        showIcon
        banner
        style={{ marginBottom: 12, borderRadius: 4 }}
      />
    );
  })();

  const executionFailureBanner = hasExecutionFailure ? (
    <Alert
      type="error"
      icon={<WarningFilled />}
      message="验证执行失败"
      description={(
        <div>
          <div>负责验证航班的 Agent 没有产出可信结论；这表示验证结论缺失或不可信，不等于金额不通过。</div>
          <ExecutionFailureDetails failures={executionFailures} />
        </div>
      )}
      showIcon
      banner
      style={{ marginBottom: 12, borderRadius: 4 }}
    />
  ) : null;

  return (
    <Drawer
      title={
        <span>
          <RocketOutlined style={{ marginRight: 8 }} />
          航班 {flight_number} 费用详情
        </span>
      }
      placement="right"
      width="min(1120px, 96vw)"
      open={visible}
      onClose={onClose}
      destroyOnClose
    >
      {/* 验证状态 Banner */}
      {executionFailureBanner}
      {validationBanner}

      {/* 航班基本信息 */}
      <Descriptions
        size="small"
        column={2}
        style={{ marginBottom: 16 }}
      >
        <Descriptions.Item
          label={<span><CalendarOutlined /> 日期</span>}
        >
          {flight_date}
        </Descriptions.Item>
        <Descriptions.Item
          label={<span><EnvironmentOutlined /> 航路</span>}
        >
          {departure_airport} → {arrival_airport}
        </Descriptions.Item>
        <Descriptions.Item label="机号">
          {aircraft_reg}
        </Descriptions.Item>
        <Descriptions.Item label="费用合计（不含参照）">
          <Text strong style={{ fontSize: 16, color: '#1890ff' }}>
            {formatAmount(total_amount)}
          </Text>
          {flight.reference_total_amount > 0 && (
            <Text type="secondary" style={{ marginLeft: 8, fontSize: 12 }}>
              参照 {formatAmount(flight.reference_total_amount)}
            </Text>
          )}
        </Descriptions.Item>
      </Descriptions>

      {/* 费用明细表格 */}
      {chargeList.length > 0 ? (
        <Table
          columns={columns}
          dataSource={chargeList}
          pagination={false}
          size="small"
          scroll={{ x: 980 }}
        />
      ) : (
        <Empty description="该航班暂无费用记录" />
      )}

      {/* ===== Agent 验证鉴定结果 ===== */}
      {flightValidation && (
        <>
          {/* ── 区域一：计费规则验证（逐项费用校验） ── */}
          {flightValidation?.charge_results?.length > 0 && (
            <>
              <Divider style={{ margin: '16px 0 12px' }}>
                <CalculatorOutlined style={{ marginRight: 4, color: '#1890ff' }} />
                计费规则验证
              </Divider>
              <div style={{ marginBottom: 16 }}>
                {flightValidation.charge_results.map((cr, idx) => {
                  const isPass = cr.result === 'PASS';
                  const isFail = cr.result === 'FAIL';
                  const isNoRule = cr.result === 'NO_RULE';
                  const borderColor = isFail ? '#ffccc7' : isNoRule ? '#ffe58f' : '#b7eb8f';
                  const bgColor = isFail ? '#fff2f0' : isNoRule ? '#fffbe6' : '#f6ffed';
                  const iconColor = isFail ? '#ff4d4f' : isNoRule ? '#faad14' : '#52c41a';
                  const statusIcon = isFail ? <CloseCircleFilled style={{ color: iconColor, fontSize: 13 }} />
                    : isNoRule ? <WarningFilled style={{ color: iconColor, fontSize: 13 }} />
                    : <CheckCircleFilled style={{ color: iconColor, fontSize: 13 }} />;
                  const productInfo = products?.[cr.product_code] || {};
                  const displayName = productInfo.name || cr.product_code;

                  return (
                    <div
                      key={idx}
                      style={{
                        padding: '8px 12px',
                        marginBottom: 6,
                        borderRadius: 6,
                        border: `1px solid ${borderColor}`,
                        background: bgColor,
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                        {statusIcon}
                        <Text strong style={{ fontSize: 13 }}>{displayName}</Text>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          {formatAmount(cr.amount)}
                        </Text>
                        <ValidationResultTag result={cr.result} />
                        {cr.brief && (
                          <Tag color="blue" style={{ fontSize: 10, margin: 0 }}>
                            {cr.brief}
                          </Tag>
                        )}
                      </div>
                      <div style={{ paddingLeft: 21, fontSize: 12, color: '#595959', lineHeight: 1.6 }}>
                        {cr.reasoning}
                      </div>
                      {cr.source && (
                        <div style={{ paddingLeft: 21, fontSize: 11, color: '#bfbfbf', marginTop: 2 }}>
                          来源: {cr.source}
                          {cr.rule_code && <span style={{ marginLeft: 8 }}>规则: {cr.rule_code}</span>}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}

          {/* ── 区域二：航班匹配验证（完整性 + 重复 + 交叉验证） ── */}
          {(flightValidation?.batchValidation?.completeness_check
            || flightValidation?.batchValidation?.duplicate_check
            || crossValidationMatrix) && (
            <>
              <Divider style={{ margin: '16px 0 12px' }}>
                <AirplaneOutlined style={{ marginRight: 4, color: '#722ed1', fontSize: 14 }} />
                航班匹配验证
              </Divider>
              <div className="validation-section">
                <CheckResultDisplay
                  label="航班费用完整性"
                  icon={<SafetyOutlined style={{ color: '#1890ff', fontSize: 12 }} />}
                  check={flightValidation?.batchValidation?.completeness_check}
                />
                <CheckResultDisplay
                  label="重复收费检查"
                  icon={<SafetyOutlined style={{ color: '#722ed1', fontSize: 12 }} />}
                  check={flightValidation?.batchValidation?.duplicate_check}
                />

                {/* 交叉验证矩阵 */}
                {crossValidationMatrix && (
                  <div style={{ marginTop: 8 }}>
                    <AgentComparisonMatrix
                      productsMatrix={crossValidationMatrix}
                      compact={true}
                      voteCount={flightValidation?.crossValidation?.vote_count || 0}
                    />
                  </div>
                )}
              </div>
            </>
          )}
        </>
      )}
    </Drawer>
  );
};

export default DetailDrawer;
